import React, { useEffect, useState } from 'react';
import { axiosKK } from '../../../services/networkRequest';
import errorMessage from '../../../services/errorMessage';
import { fetchHubspotOptions, partnerOptions } from '../../../services/hubspotSync';
import HubspotNameField from '../../../components/admin/HubspotNameField';
import Button from '../../../components/Button';
import { AdminNotice } from '../../../components/admin/Shared';

interface Props {
  partnerId: number;
  partnerName?: string;
  hubspotName?: string | null;
  onSaved: () => void;
}

/**
 * The HubSpot Collection Courier / Return Courier option this partner stands for.
 * Saving it hands the partner every live order HubSpot already gives that option
 * to, and from then on its assignments here are sent to HubSpot under that name.
 */
export default function PartnerHubspotLink({ partnerId, partnerName, hubspotName, onSaved }: Props) {
  const [options, setOptions] = useState<string[]>([]);
  const [value, setValue] = useState(hubspotName || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchHubspotOptions().then((o) => {
      if (!cancelled) setOptions(partnerOptions(o));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => setValue(hubspotName || ''), [hubspotName, partnerId]);

  const changed = (value || '') !== (hubspotName || '');

  const save = () => {
    setBusy(true);
    setError(undefined);
    setSaved(false);
    axiosKK
      .patch(`/admin/partners/${partnerId}`, { hubspotName: value.trim() || null })
      .then(() => {
        setSaved(true);
        onSaved();
      })
      .catch((err) => errorMessage(setError)(err))
      .finally(() => setBusy(false));
  };

  return (
    <div className="admin-partner-detail__hubspot" data-testid="partner-hubspot-link">
      <HubspotNameField
        id={`partner-${partnerId}-hubspot-name`}
        label="HubSpot name (Collection Courier and Return Courier)"
        value={value}
        onChange={(v) => {
          setValue(v);
          setSaved(false);
        }}
        options={options}
        disabled={busy}
      />
      <Button type="button" variant="secondary" size="sm" onClick={save} disabled={busy || !changed}>
        {busy ? 'Saving…' : 'Save HubSpot name'}
      </Button>
      <AdminNotice intent="error">{error}</AdminNotice>
      {saved && (
        <AdminNotice intent="success">
          {value
            ? `Saved. Live orders HubSpot gives to ${value} are now assigned to ${partnerName || 'this partner'}.`
            : 'Saved. This partner is no longer linked to HubSpot.'}
        </AdminNotice>
      )}
    </div>
  );
}
